import { Link } from 'react-router-dom';
import { useState, useRef, useEffect, useMemo } from 'react';

function Home() {
  const projects = useMemo(() => [
    {
      id: 1,
      title: "Rope Wall Feature",
      images: ["RopeWall1.JPEG", "RopeWall2.JPEG"],
      tag: "Custom Buildouts",
      description: "Textured wall treatment with natural rope materials."
    },
    {
      id: 2,
      title: "Midnight Animal",
      images: ["Midnight Animal 1.JPEG", "Midnight Animal 2.JPG", "Midnight Animal 3.jpeg", "Midnight Animal 4.JPG"],
      tag: "Custom Buildouts",
      description: "Custom bar and restaurant build-out."
    },
    {
      id: 3,
      title: "Sextant Light",
      images: ["sextant light.JPEG", "sextant 2.jpg"],
      tag: "Lighting",
      description: "Driftwood pendant lights with living plants, creating a natural atmosphere."
    },
    {
      id: 4,
      title: "Quadlight Fixture",
      images: ["quadlight 1.jpg", "quad light2.JPEG", "Quadlight 3.jpeg"],
      tag: "Lighting",
      description: "Modern lighting design with reclaimed materials."
    },
    {
      id: 5,
      title: "Trip Light",
      images: ["trip light.JPEG", "trip light 3.JPEG"],
      tag: "Lighting",
      description: "Custom chandelier crafted from salvaged elements."
    },
    {
      id: 6,
      title: "Bowtie Joinery",
      images: ["Bowtie1.JPEG", "Bowtie2 3.4.JPEG"],
      tag: "Custom Buildouts",
      description: "Traditional butterfly joinery in handcrafted live edge furniture."
    },
    {
      id: 7,
      title: "Boat Bench Restoration",
      images: ["Boat Bench Restoration.JPG", "Boat Bench Restoration 2.JPG"],
      tag: "Restorations",
      description: "Classic boat bench restoration with careful attention to detail."
    },
    {
      id: 8,
      title: "Gon Bop Restoration",
      images: ["Gon Bop restoration.JPG", "Gon Bop restoration 2.JPG"],
      tag: "Restorations",
      description: "Vintage Gon Bop drum restoration."
    },
    {
      id: 9,
      title: "Twin Pendants",
      images: ["twinlight.JPEG", "twin3.JPEG"],
      tag: "Lighting",
      description: "Contemporary pendant fixtures for bar and dining spaces."
    },
    {
      id: 10,
      title: "Floating Cabinets",
      images: ["Floating cabinets.JPG"],
      tag: "Custom Buildouts",
      description: "Custom floating cabinet installation."
    },
    {
      id: 11,
      title: "Bamboo Pergola",
      images: ["Pergola2.JPEG"],
      tag: "Custom Buildouts",
      description: "Natural bamboo construction for outdoor living spaces."
    },
    {
      id: 12,
      title: "Teak Ceiling",
      images: ["indoteak cieling.JPEG"],
      tag: "Custom Buildouts",
      description: "Indonesian reclaimed teak ceiling installation."
    }
  ], []);

  const [currentImages, setCurrentImages] = useState(() =>
    projects.reduce((acc, project) => {
      acc[project.id] = 0;
      return acc;
    }, {})
  );

  const [fadingProjects, setFadingProjects] = useState({});

  const intervalsRef = useRef({});
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [nextHeroImageIndex, setNextHeroImageIndex] = useState(1);
  const [heroTransitioning, setHeroTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [modalProjectImages, setModalProjectImages] = useState({});
  const [lightboxProject, setLightboxProject] = useState(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  // Hero images - just add or remove filenames from this list
  const heroImages = useMemo(() => [
    "trip light.JPEG",
    "RopeWall1.JPEG",
    "Boat Bench Restoration.JPG"
  ], []);

  // Desktop static image configuration
  const desktopConfig = useMemo(() => ({
    hero: {
      image: "trip light.JPEG",  // Which hero image to show on desktop
      position: "center 40%"        // How to position it (horizontal%, vertical%)
      // Examples: "center top", "center bottom", "left center", "50% 30%"
    },
    categories: {
      'lighting': {
        image: "sextant light.JPEG",  // Use any image from your projects
        position: "center 35%"
      },
      'custom-buildouts': {
        image: "RopeWall1.JPEG",
        position: "center center"
      },
      'restorations': {
        image: "Boat Bench Restoration.JPG",
        position: "center 40%"
      }
    }
  }), []);

  // Category data with cycling images
  const categories = useMemo(() => [
    {
      id: 'lighting',
      title: 'Lighting',
      description: 'Handcrafted illumination with reclaimed materials and natural elements.',
      projects: projects.filter(p => p.tag === 'Lighting')
    },
    {
      id: 'custom-buildouts',
      title: 'Custom Buildouts',
      description: 'Bespoke furniture, installations, and architectural elements crafted with traditional joinery and artisanal detail.',
      projects: projects.filter(p => p.tag === 'Custom Buildouts')
    },
    {
      id: 'restorations',
      title: 'Restorations',
      description: 'Breathing new life into cherished pieces with careful restoration.',
      projects: projects.filter(p => p.tag === 'Restorations')
    }
  ], [projects]);

  // Category image cycling state
  const [categoryImageIndices, setCategoryImageIndices] = useState(() =>
    categories.reduce((acc, cat) => {
      acc[cat.id] = { current: 0, next: 1 };
      return acc;
    }, {})
  );
  const [categoryTransitioning, setCategoryTransitioning] = useState({});

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  // UNIFIED cycling system - manages both hero and category sections
  const heroIntervalRef = useRef(null);
  const categoryIntervalRef = useRef(null);
  const activeSectionIdRef = useRef(null); // Track which section is active: 'hero' or category id
  const categoriesRef = useRef(categories);

  // Keep categories ref updated
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  useEffect(() => {
    console.log('[CYCLING] Unified effect running');

    let scrollTimeout = null;
    let isProcessing = false;

    // Hero cycling functions
    const startHeroCycling = () => {
      if (heroIntervalRef.current) {
        clearInterval(heroIntervalRef.current);
      }

      heroIntervalRef.current = setInterval(() => {
        console.log('[CYCLING] Hero transitioning');
        setHeroTransitioning(true);

        setTimeout(() => {
          setHeroImageIndex(prev => (prev + 1) % heroImages.length);
          setHeroTransitioning(false);

          setTimeout(() => {
            setNextHeroImageIndex(prev => (prev + 1) % heroImages.length);
          }, 1000);
        }, 1000);
      }, 5000);

      console.log('[CYCLING] Hero started');
    };

    const stopHeroCycling = () => {
      if (heroIntervalRef.current) {
        console.log('[CYCLING] Hero stopped');
        clearInterval(heroIntervalRef.current);
        heroIntervalRef.current = null;
      }
    };

    // Category cycling - matches hero pattern exactly
    const startCyclingSection = (category) => {
      const sectionId = category.id;
      const allImages = category.projects.map(p => p.images[0]).filter(Boolean);

      if (allImages.length <= 1) return;

      console.log('[CATEGORY] Starting:', sectionId);

      if (categoryIntervalRef.current) {
        clearInterval(categoryIntervalRef.current);
        categoryIntervalRef.current = null;
      }

      categoryIntervalRef.current = setInterval(() => {
        console.log('[CATEGORY] Transition start:', sectionId);
        setCategoryTransitioning(prev => ({ ...prev, [sectionId]: true }));

        setTimeout(() => {
          // Update current index
          setCategoryImageIndices(prev => ({
            ...prev,
            [sectionId]: {
              current: (prev[sectionId].current + 1) % allImages.length,
              next: prev[sectionId].next
            }
          }));
          setCategoryTransitioning(prev => ({ ...prev, [sectionId]: false }));

          setTimeout(() => {
            // Update next index (increment from previous next, not from current)
            setCategoryImageIndices(prev => ({
              ...prev,
              [sectionId]: {
                current: prev[sectionId].current,
                next: (prev[sectionId].next + 1) % allImages.length
              }
            }));
          }, 1000);
        }, 1000);
      }, 5000);
    };

    const stopCycling = () => {
      if (categoryIntervalRef.current) {
        console.log('[CATEGORY] Stopping cycling');
        clearInterval(categoryIntervalRef.current);
        categoryIntervalRef.current = null;
      }
    };

    const updateActiveSection = () => {
      if (isProcessing) return;
      isProcessing = true;

      const windowHeight = window.innerHeight;
      let winner = { id: null, visibleHeight: 0 };

      // Check HERO visibility
      const heroElement = document.querySelector('.hero-section');
      if (heroElement) {
        const rect = heroElement.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);

        if (visibleHeight > 0) {
          winner = { id: 'hero', visibleHeight };
        }
      }

      // Check ALL CATEGORY visibility
      categoriesRef.current.forEach(category => {
        const element = document.querySelector(`[data-category="${category.id}"]`);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);

        // Only consider if more visible than current winner
        if (visibleHeight > winner.visibleHeight) {
          winner = { id: category.id, visibleHeight, category };
        }
      });

      // If the winner changed, switch sections
      if (winner.id !== activeSectionIdRef.current) {
        console.log('[CYCLING] Switching to:', winner.id || 'none');

        // Stop ALL intervals
        stopHeroCycling();
        stopCycling();

        // Update active section
        activeSectionIdRef.current = winner.id;

        // Start the winner
        if (winner.id === 'hero') {
          startHeroCycling();
        } else if (winner.id && winner.category) {
          startCyclingSection(winner.category);
        }
      }

      isProcessing = false;
    };

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateActiveSection, 200);
    };

    // Initial check - start with hero
    setTimeout(updateActiveSection, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      console.log('[CYCLING] Cleanup');
      window.removeEventListener('scroll', handleScroll);
      stopHeroCycling();
      stopCycling();
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []); // Empty deps - won't re-run

  // Auto-cycle project images on mobile
  useEffect(() => {
    if (!isMobile) return;

    const startCycling = (projectId, imagesLength) => {
      if (imagesLength <= 1) return;

      const interval = setInterval(() => {
        // Start fade out
        setFadingProjects(prev => ({ ...prev, [projectId]: true }));

        // Change image mid-fade
        setTimeout(() => {
          setCurrentImages(prev => ({
            ...prev,
            [projectId]: (prev[projectId] + 1) % imagesLength
          }));

          // Fade back in
          setTimeout(() => {
            setFadingProjects(prev => ({ ...prev, [projectId]: false }));
          }, 100);
        }, 400);
      }, 1500); // Faster for mobile - 1.5 seconds

      intervalsRef.current[projectId] = interval;
    };

    // Start cycling for all projects with multiple images
    projects.forEach(project => {
      if (project.images.length > 1) {
        startCycling(project.id, project.images.length);
      }
    });

    // Cleanup
    return () => {
      projects.forEach(project => {
        if (intervalsRef.current[project.id]) {
          clearInterval(intervalsRef.current[project.id]);
        }
      });
    };
  }, [isMobile, projects]);

  // Modal project cycling - only most visible project cycles (like main page)
  const modalIntervalRef = useRef(null);
  const activeModalProjectRef = useRef(null);

  useEffect(() => {
    if (!activeCategory) {
      setModalProjectImages({});
      activeModalProjectRef.current = null;
      return;
    }

    // Initialize image indices for all projects
    const initialIndices = {};
    activeCategory.projects.forEach(project => {
      initialIndices[project.id] = 0;
    });
    setModalProjectImages(initialIndices);

    let scrollTimeout = null;
    let isProcessing = false;

    const startCyclingProject = (project) => {
      if (project.images.length <= 1) return;

      console.log('[MODAL] Starting:', project.title);

      if (modalIntervalRef.current) {
        clearInterval(modalIntervalRef.current);
        modalIntervalRef.current = null;
      }

      modalIntervalRef.current = setInterval(() => {
        setModalProjectImages(prev => ({
          ...prev,
          [project.id]: (prev[project.id] + 1) % project.images.length
        }));
      }, 2500);
    };

    const stopCycling = () => {
      if (modalIntervalRef.current) {
        clearInterval(modalIntervalRef.current);
        modalIntervalRef.current = null;
      }
    };

    const findMostVisibleProject = () => {
      if (isProcessing) return;
      isProcessing = true;

      const modalContent = document.querySelector('.modal-content');
      if (!modalContent) {
        isProcessing = false;
        return;
      }

      const modalRect = modalContent.getBoundingClientRect();
      let winner = { project: null, visibleHeight: 0 };

      activeCategory.projects.forEach(project => {
        const element = document.querySelector(`[data-project-id="${project.id}"]`);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, modalRect.top);
        const visibleBottom = Math.min(rect.bottom, modalRect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > winner.visibleHeight) {
          winner = { project, visibleHeight };
        }
      });

      if (winner.project && winner.project.id !== activeModalProjectRef.current) {
        stopCycling();
        activeModalProjectRef.current = winner.project.id;
        startCyclingProject(winner.project);
      }

      isProcessing = false;
    };

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(findMostVisibleProject, 200);
    };

    const modalContent = document.querySelector('.modal-content');
    if (modalContent) {
      modalContent.addEventListener('scroll', handleScroll, { passive: true });
      setTimeout(findMostVisibleProject, 100);
    }

    return () => {
      if (modalContent) {
        modalContent.removeEventListener('scroll', handleScroll);
      }
      stopCycling();
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [activeCategory]);

  // Handle hash navigation on page load
  useEffect(() => {
    if (window.location.hash === '#work') {
      // Use instant scroll on mount to avoid the scroll-to-top then scroll-down effect
      const workSection = document.getElementById('work');
      if (workSection) {
        workSection.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
  }, []);

  // Handle hash changes (when already on the page)
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#work') {
        const workSection = document.getElementById('work');
        if (workSection) {
          workSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div>
      <header>
        <div className="header-container">
          <Link to="/" className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            RAOC Studio
          </Link>
          <nav>
            <a href="#work" onClick={(e) => {
              e.preventDefault();
              const workSection = document.getElementById('work');
              if (workSection) {
                workSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}>Work</a>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <a
            href="https://instagram.com/raocstudio"
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-link"
            aria-label="Follow us on Instagram"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
            </svg>
          </a>
          <div className="header-actions">
            <Link to="/contact" className="btn">Start a Project</Link>
          </div>
        </div>
      </header>

      <section id="home" className="hero-section">
        <div
          className="hero-bg hero-bg-current"
          style={{ backgroundImage: `url("/assets/${heroImages[heroImageIndex]}")` }}
        ></div>
        <div
          className={`hero-bg hero-bg-next ${heroTransitioning ? 'visible' : ''}`}
          style={{ backgroundImage: `url("/assets/${heroImages[nextHeroImageIndex]}")` }}
        ></div>
        <div className="hero-container">
          <div className="hero-content">
            <span className="tag">Random Acts of Craftsmanship</span>
            <h1>
              Craft with integrity. Build with soul.
            </h1>
            <p>
              RAOC Studio blends traditional Japanese joinery with modern restoration and design.
            </p>
          </div>
        </div>
      </section>

      {/* Category Sections */}
      {categories.map((category, index) => {
        // Get only the FIRST image from each project (one per project) - matches cycling logic
        const allImages = category.projects.map(p => p.images[0]).filter(Boolean);
        const currentIdx = categoryImageIndices[category.id]?.current || 0;
        const nextIdx = categoryImageIndices[category.id]?.next || 1;
        const isTransitioning = categoryTransitioning[category.id];
        const currentImage = allImages[currentIdx] || allImages[0];
        const nextImage = allImages[nextIdx] || allImages[1] || allImages[0];

        return (
          <section
            key={category.id}
            id={index === 0 ? 'work' : undefined}
            data-category={category.id}
            className="category-section"
            onClick={() => setActiveCategory(category)}
            style={{
              cursor: 'pointer',
              background: allImages.length === 0 ? '#1a1a1a' : undefined
            }}
          >
            {currentImage ? (
              <>
                <div
                  className="hero-bg hero-bg-current"
                  style={{ backgroundImage: `url("/assets/${currentImage}")` }}
                ></div>
                {nextImage && (
                  <div
                    className={`hero-bg hero-bg-next ${isTransitioning ? 'visible' : ''}`}
                    style={{ backgroundImage: `url("/assets/${nextImage}")` }}
                  ></div>
                )}
              </>
            ) : (
              <div className="hero-bg hero-bg-current" style={{ background: '#1a1a1a' }}></div>
            )}
            <div className="hero-container">
              <div className="hero-content">
                <h2>{category.title}</h2>
                <p>{category.description}</p>
              </div>
            </div>
          </section>
        );
      })}

      {/* Modal */}
      {activeCategory && (
        <div className="modal-overlay" onClick={() => setActiveCategory(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveCategory(null)}>
              ×
            </button>
            <div className="modal-header">
              <h2>{activeCategory.title}</h2>
              <p>{activeCategory.description}</p>
            </div>
            <div className="modal-grid">
              {activeCategory.projects.map((project, index) => {
                const currentImageIndex = modalProjectImages[project.id] || 0;
                const currentImage = project.images[currentImageIndex] || project.images[0];
                const isPortrait = project.images[0] &&
                  ['sextant light.JPEG', 'quadlight 1.jpg', 'trip light.JPEG', 'Bowtie1.JPEG',
                   'Boat Bench Restoration.JPG', 'Gon Bop restoration.JPG', 'RopeWall1.JPEG',
                   'Midnight Animal 1.JPEG'].some(img => project.images[0].includes(img.split('.')[0]));

                return (
                  <div
                    key={project.id}
                    className="card project-card"
                    data-orientation={isPortrait ? 'portrait' : 'landscape'}
                    data-project-id={project.id}
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      minHeight: '400px'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxProject(project);
                        setLightboxImageIndex(currentImageIndex);
                      }}
                    >
                      <img
                        src={`/assets/${currentImage}`}
                        alt={project.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
                        pointerEvents: 'none'
                      }}></div>
                    </div>
                    <div style={{
                      position: 'relative',
                      zIndex: 2,
                      padding: '1.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      height: '100%',
                      color: 'white',
                      pointerEvents: 'none'
                    }}>
                      <span className="tag" style={{
                        color: 'rgba(255,255,255,0.8)',
                        marginBottom: '0.5rem',
                        fontSize: '0.688rem'
                      }}>{project.tag}</span>
                      <h3 style={{
                        color: 'white',
                        fontSize: '1.25rem',
                        margin: 0
                      }}>{project.title}</h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox for full-screen images with navigation */}
      {lightboxProject && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={() => setLightboxProject(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxProject(null)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '3rem',
              cursor: 'pointer',
              zIndex: 2001,
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              padding: 0
            }}
          >
            ×
          </button>

          {/* Previous arrow */}
          {lightboxProject.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImageIndex(prev =>
                  prev === 0 ? lightboxProject.images.length - 1 : prev - 1
                );
              }}
              style={{
                position: 'absolute',
                left: '1rem',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                fontSize: '2rem',
                cursor: 'pointer',
                zIndex: 2001,
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              ‹
            </button>
          )}

          {/* Next arrow */}
          {lightboxProject.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImageIndex(prev =>
                  prev === lightboxProject.images.length - 1 ? 0 : prev + 1
                );
              }}
              style={{
                position: 'absolute',
                right: '1rem',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                fontSize: '2rem',
                cursor: 'pointer',
                zIndex: 2001,
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              ›
            </button>
          )}

          {/* Image */}
          <div onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={`/assets/${lightboxProject.images[lightboxImageIndex]}`}
              alt={lightboxProject.title}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
            />
            {/* Image counter and title */}
            <div style={{ marginTop: '1rem', color: 'white', textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{lightboxProject.title}</h3>
              {lightboxProject.images.length > 1 && (
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
                  {lightboxImageIndex + 1} / {lightboxProject.images.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <footer>
        <div className="footer-container">
          <div>© 2025 RAOC Studio — Craftsmanship • Quality • Integrity • Tradition • Artistry</div>
          <div className="footer-links">
            <a href="#work">Work</a>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
